import { toast } from "sonner";
import { create } from "zustand";
import { useAuthStore } from "@/features/auth/store/auth-store";
import {
  addMembers as apiAddMembers,
  createConversation as apiCreateConversation,
  deleteConversation as apiDeleteConversation,
  deleteMessage as apiDeleteMessage,
  editMessage as apiEditMessage,
  getPresence,
  getUnread,
  leaveConversation as apiLeaveConversation,
  listConversations,
  listMessages,
  markConversationRead,
  removeMember as apiRemoveMember,
  renameConversation as apiRenameConversation,
  sendMessage as apiSendMessage,
  setMemberRole as apiSetMemberRole,
  toMessage,
  type CreateConversationPayload,
} from "@/features/chat/api";
import type { ChatEvent, Conversation, MemberRole, Message } from "@/features/chat/types";

const TYPING_TTL_MS = 4000;

// --- module-level (non-reactive) socket + timer state ----------------------
let socket: WebSocket | null = null;
let subscribers = 0;
let manualClose = false;
let reconnectAttempts = 0;
// Consecutive auth (4401) failures — bounds the refresh-and-retry loop so a dead
// refresh token can't spin forever hammering the WS + REST endpoints.
let authFailures = 0;
const MAX_AUTH_FAILURES = 5;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

function meId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

function wsUrl(): string | null {
  const token = useAuthStore.getState().accessToken;
  if (!token) return null;
  const base = process.env.BUN_PUBLIC_API_URL ?? "http://localhost:8000/api/v1";
  // BUN_PUBLIC_API_URL is a relative path in production (same-origin, "/api/v1")
  // but an absolute URL in dev — window.location.origin as the base resolves
  // both correctly (and is ignored when `base` is already absolute).
  const u = new URL(base, window.location.origin);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}/ws/chat/?token=${encodeURIComponent(token)}`;
}

function sortConversations(list: Conversation[]): Conversation[] {
  return [...list].sort((a, b) => {
    const at = a.lastMessageAt ?? a.createdAt;
    const bt = b.lastMessageAt ?? b.createdAt;
    return bt.localeCompare(at);
  });
}

interface ChatState {
  connected: boolean;
  conversations: Conversation[];
  activeId: string | null;
  messagesByConv: Record<string, Message[]>;
  msgPage: Record<string, { nextPage: number | null; loading: boolean }>;
  onlineIds: string[];
  typing: Record<string, Record<string, string>>;
  unreadTotal: number;
  loadingConversations: boolean;

  acquire: () => void;
  release: () => void;
  loadConversations: () => Promise<void>;
  openConversation: (id: string) => Promise<void>;
  loadOlder: (id: string) => Promise<void>;
  send: (id: string, body: string) => Promise<void>;
  editMessage: (id: string, messageId: string, body: string) => Promise<void>;
  deleteMessage: (id: string, messageId: string) => Promise<void>;
  createConversation: (payload: CreateConversationPayload) => Promise<Conversation>;
  leaveConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, name: string) => Promise<void>;
  addMembers: (id: string, memberIds: string[]) => Promise<void>;
  removeMember: (id: string, userId: string) => Promise<void>;
  setMemberRole: (id: string, userId: string, role: MemberRole) => Promise<void>;
  sendTyping: (id: string, isTyping: boolean) => void;
  handleEvent: (event: ChatEvent) => void;
  isOnline: (userId: string) => boolean;
}

export const useChatStore = create<ChatState>((set, get) => ({
  connected: false,
  conversations: [],
  activeId: null,
  messagesByConv: {},
  msgPage: {},
  onlineIds: [],
  typing: {},
  unreadTotal: 0,
  loadingConversations: false,

  acquire: () => {
    subscribers += 1;
    if (subscribers === 1) {
      manualClose = false;
      openSocket();
      // Resync REST state whenever the first consumer mounts. These are
      // best-effort — swallow failures (e.g. backend offline) so they don't
      // surface as unhandled promise rejections.
      void get().loadConversations();
      void getUnread()
        .then((u) => set({ unreadTotal: u.total }))
        .catch(() => {});
      void getPresence()
        .then((ids) => set({ onlineIds: ids }))
        .catch(() => {});
    }
  },

  release: () => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers === 0) closeSocket();
  },

  loadConversations: async () => {
    set({ loadingConversations: true });
    try {
      const conversations = sortConversations(await listConversations());
      set({ conversations, loadingConversations: false });
    } catch {
      set({ loadingConversations: false });
    }
  },

  openConversation: async (id) => {
    set({ activeId: id });
    subscribe(id);
    if (!get().messagesByConv[id]) {
      const page = await listMessages(id, 1);
      set((s) => ({
        messagesByConv: { ...s.messagesByConv, [id]: page.messages },
        msgPage: { ...s.msgPage, [id]: { nextPage: page.nextPage, loading: false } },
      }));
    }
    // Clear local unread for this conversation immediately; the server confirms.
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === id ? { ...c, unreadCount: 0 } : c,
      ),
    }));
    try {
      const unread = await markConversationRead(id);
      set({ unreadTotal: unread.total });
    } catch {
      /* non-critical */
    }
  },

  loadOlder: async (id) => {
    const meta = get().msgPage[id];
    if (!meta || meta.nextPage === null || meta.loading) return;
    set((s) => ({ msgPage: { ...s.msgPage, [id]: { ...meta, loading: true } } }));
    const page = await listMessages(id, meta.nextPage);
    set((s) => ({
      messagesByConv: { ...s.messagesByConv, [id]: [...page.messages, ...(s.messagesByConv[id] ?? [])] },
      msgPage: { ...s.msgPage, [id]: { nextPage: page.nextPage, loading: false } },
    }));
  },

  send: async (id, body) => {
    const message = await apiSendMessage(id, body);
    addMessage(set, get, message);
  },

  editMessage: async (id, messageId, body) => {
    const message = await apiEditMessage(id, messageId, body);
    patchMessage(set, message);
  },

  deleteMessage: async (id, messageId) => {
    await apiDeleteMessage(id, messageId);
    // The WS message.deleted event tombstones it for everyone (incl. us).
  },

  leaveConversation: async (id) => {
    await apiLeaveConversation(id);
    dropConversation(set, get, id);
  },

  deleteConversation: async (id) => {
    await apiDeleteConversation(id);
    dropConversation(set, get, id);
  },

  renameConversation: async (id, name) => {
    const conversation = await apiRenameConversation(id, name);
    mergeConversation(set, conversation);
  },

  addMembers: async (id, memberIds) => {
    const conversation = await apiAddMembers(id, memberIds);
    mergeConversation(set, conversation);
  },

  removeMember: async (id, userId) => {
    const conversation = await apiRemoveMember(id, userId);
    mergeConversation(set, conversation);
  },

  setMemberRole: async (id, userId, role) => {
    const conversation = await apiSetMemberRole(id, userId, role);
    mergeConversation(set, conversation);
  },

  createConversation: async (payload) => {
    const conversation = await apiCreateConversation(payload);
    set((s) => ({
      conversations: sortConversations([
        conversation,
        ...s.conversations.filter((c) => c.id !== conversation.id),
      ]),
    }));
    return conversation;
  },

  sendTyping: (id, isTyping) => {
    send({ type: "typing", conversation_id: id, is_typing: isTyping });
  },

  handleEvent: (event) => {
    switch (event.type) {
      case "message.new":
        addMessage(set, get, toMessage(event.payload));
        break;
      case "message.edited":
      case "message.deleted":
        patchMessage(set, toMessage(event.payload));
        break;
      case "conversation.updated":
        // Membership/name/role changed — re-pull the (small) conversation list.
        void get().loadConversations();
        break;
      case "conversation.removed":
        dropConversation(set, get, event.payload.conversation_id);
        break;
      case "unread.update":
        set({ unreadTotal: event.payload.total });
        set((s) => ({
          conversations: s.conversations.map((c) => ({
            ...c,
            unreadCount: event.payload.per_conversation[c.id] ?? c.unreadCount,
          })),
        }));
        break;
      case "presence.update":
        set((s) => {
          const online = new Set(s.onlineIds);
          if (event.payload.online) online.add(event.payload.user_id);
          else online.delete(event.payload.user_id);
          return { onlineIds: [...online] };
        });
        break;
      case "typing":
        applyTyping(set, event.payload);
        break;
      case "message.read":
        // Read receipts aren't rendered per-message in v1; unread.update drives
        // the badge. No-op for now.
        break;
    }
  },

  isOnline: (userId) => get().onlineIds.includes(userId),
}));

// --- message helpers -------------------------------------------------------

function patchMessage(
  set: (fn: (s: ChatState) => Partial<ChatState>) => void,
  message: Message,
) {
  // Replace an edited/deleted message in its loaded thread (no-op if not loaded).
  set((s) => {
    const thread = s.messagesByConv[message.conversation];
    if (!thread) return {};
    return {
      messagesByConv: {
        ...s.messagesByConv,
        [message.conversation]: thread.map((m) => (m.id === message.id ? message : m)),
      },
    };
  });
}

function mergeConversation(
  set: (fn: (s: ChatState) => Partial<ChatState>) => void,
  conversation: Conversation,
) {
  set((s) => ({
    conversations: sortConversations([
      conversation,
      ...s.conversations.filter((c) => c.id !== conversation.id),
    ]),
  }));
}

function dropConversation(
  set: (fn: (s: ChatState) => Partial<ChatState>) => void,
  get: () => ChatState,
  id: string,
) {
  if (get().activeId === id) {
    unsubscribe(id);
  }
  set((s) => {
    const messagesByConv = { ...s.messagesByConv };
    delete messagesByConv[id];
    const msgPage = { ...s.msgPage };
    delete msgPage[id];
    return {
      conversations: s.conversations.filter((c) => c.id !== id),
      messagesByConv,
      msgPage,
      activeId: s.activeId === id ? null : s.activeId,
    };
  });
}

function addMessage(
  set: (fn: (s: ChatState) => Partial<ChatState>) => void,
  get: () => ChatState,
  message: Message,
) {
  const state = get();
  const convId = message.conversation;
  const existing = state.messagesByConv[convId];

  // Update the loaded thread (dedupe by id — the sender gets both the POST
  // result and the WS echo).
  if (existing) {
    if (!existing.some((m) => m.id === message.id)) {
      set((s) => ({
        messagesByConv: { ...s.messagesByConv, [convId]: [...(s.messagesByConv[convId] ?? []), message] },
      }));
    }
  }

  // Update the conversation-list preview + re-sort.
  const known = state.conversations.some((c) => c.id === convId);
  if (known) {
    set((s) => ({
      conversations: sortConversations(
        s.conversations.map((c) =>
          c.id === convId
            ? {
                ...c,
                lastMessage: { body: message.body, senderName: message.senderName, at: message.createdAt },
                lastMessageAt: message.createdAt,
              }
            : c,
        ),
      ),
    }));
  } else {
    // A conversation we don't know about yet (someone just started one) — pull
    // the list so it appears.
    void get().loadConversations();
  }

  const fromMe = String(message.sender) === String(meId());
  const isActive = state.activeId === convId;

  if (isActive && !fromMe && document.visibilityState === "visible") {
    // We're looking at it — keep it read.
    void markConversationRead(convId)
      .then((u) => set(() => ({ unreadTotal: u.total })))
      .catch(() => {});
  } else if (!fromMe) {
    toast.message(message.senderName || "New message", {
      description: message.body.slice(0, 120),
    });
  }
}

function applyTyping(
  set: (fn: (s: ChatState) => Partial<ChatState>) => void,
  payload: { conversation_id: string; user_id: string; user_name: string; is_typing: boolean },
) {
  const key = `${payload.conversation_id}:${payload.user_id}`;
  const existingTimer = typingTimers.get(key);
  if (existingTimer) clearTimeout(existingTimer);

  const clear = () => {
    typingTimers.delete(key);
    set((s) => {
      const conv = { ...(s.typing[payload.conversation_id] ?? {}) };
      delete conv[payload.user_id];
      return { typing: { ...s.typing, [payload.conversation_id]: conv } };
    });
  };

  if (!payload.is_typing) {
    clear();
    return;
  }
  set((s) => ({
    typing: {
      ...s.typing,
      [payload.conversation_id]: {
        ...(s.typing[payload.conversation_id] ?? {}),
        [payload.user_id]: payload.user_name,
      },
    },
  }));
  typingTimers.set(key, setTimeout(clear, TYPING_TTL_MS));
}

// --- socket lifecycle ------------------------------------------------------

function subscribe(conversationId: string) {
  send({ type: "subscribe", conversation_id: conversationId });
}

function unsubscribe(conversationId: string) {
  send({ type: "unsubscribe", conversation_id: conversationId });
}

function send(obj: unknown) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(obj));
  }
}

function openSocket() {
  const url = wsUrl();
  if (!url || socket) return;

  const ws = new WebSocket(url);
  socket = ws;

  ws.onopen = () => {
    reconnectAttempts = 0;
    authFailures = 0;
    useChatStore.setState({ connected: true });
    // Re-subscribe to the open thread after a reconnect.
    const active = useChatStore.getState().activeId;
    if (active) subscribe(active);
  };

  ws.onmessage = (ev) => {
    try {
      const event = JSON.parse(ev.data) as ChatEvent;
      useChatStore.getState().handleEvent(event);
    } catch {
      /* ignore malformed frames */
    }
  };

  ws.onclose = (ev) => {
    socket = null;
    useChatStore.setState({ connected: false });
    if (manualClose || subscribers === 0) return;
    scheduleReconnect(ev.code);
  };

  ws.onerror = () => {
    // onclose fires next and handles reconnect.
    ws.close();
  };
}

function scheduleReconnect(code: number) {
  if (reconnectTimer) return;

  const reconnect = () => {
    reconnectTimer = null;
    if (subscribers === 0 || manualClose) return;
    openSocket();
  };

  // Auth failure (expired token): refresh via any authenticated request, then
  // reconnect with the rotated token. Bounded so a truly-dead session (expired
  // refresh token / server-side logout) can't loop forever — `apiFetch` clears
  // the session on a failed refresh, and we also give up after MAX_AUTH_FAILURES.
  if (code === 4401) {
    authFailures += 1;
    if (
      authFailures > MAX_AUTH_FAILURES ||
      !useAuthStore.getState().isAuthenticated
    ) {
      return;
    }
    void getUnread()
      .then((u) => useChatStore.setState({ unreadTotal: u.total }))
      .catch(() => {})
      .finally(() => {
        if (useAuthStore.getState().isAuthenticated) {
          reconnectTimer = setTimeout(reconnect, 500);
        }
      });
    return;
  }

  reconnectAttempts += 1;
  const delay = Math.min(30000, 1000 * 2 ** (reconnectAttempts - 1));
  reconnectTimer = setTimeout(reconnect, delay);
}

function closeSocket() {
  manualClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  // Cancel any in-flight typing-expiry timers so they can't fire into a torn-down
  // (or next) session's store state.
  for (const timer of typingTimers.values()) clearTimeout(timer);
  typingTimers.clear();
  if (socket) {
    socket.close();
    socket = null;
  }
  useChatStore.setState({ connected: false, typing: {} });
}
